import { forwardRef, useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { ChevronsUpDown, Check, Plus } from "lucide-react";
import { CATEGORY_CODE_MAX_DEPTH, flattenCategoryTree, getCategoryCodeSegment } from "../lib/category-tree";
import { cn, formatCategoryName, normalizeCategoryName, normalizeUppercase, stringToColor } from "../lib/utils";
import { Label } from "./ui/label";
import { MobileCommandSheet } from "./MobileCommandSheet";
import { useIsMobileCombobox } from "../hooks/use-mobile-combobox";

function normalizeCommandSearch(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleUpperCase("pt-BR");
}

function commandFilter(value, search, keywords = []) {
  const needle = normalizeCommandSearch(search);
  if (!needle) return 1;

  const haystack = normalizeCommandSearch([value, ...keywords].filter(Boolean).join(" "));
  return haystack.includes(needle) ? 1 : 0;
}

const SearchSelectTrigger = forwardRef(({
  open,
  value,
  displayValue,
  placeholder,
  testId,
  onMobileOpen,
  isMobile,
  onClick,
  ...props
}, ref) => {
  return (
    <Button
      ref={ref}
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      className={cn(
        "min-w-0 w-full justify-between bg-background font-normal text-foreground hover:bg-muted/30 hover:text-foreground",
        open && "border-primary/70 bg-background text-foreground ring-1 ring-primary/20 hover:bg-background hover:text-foreground"
      )}
      data-testid={testId}
      onClick={(event) => {
        onClick?.(event);
        if (isMobile) onMobileOpen();
      }}
      {...props}
    >
      <span className={cn(
        "truncate",
        !value && "text-muted-foreground"
      )}>
        {displayValue || placeholder}
      </span>
      <ChevronsUpDown className={cn("ml-2 h-4 w-4 shrink-0", open ? "opacity-90" : "opacity-50")} />
    </Button>
  );
});
SearchSelectTrigger.displayName = "SearchSelectTrigger";

function CategoryCommandItem({ item, selected, onSelect, onAddChild, isMobile }) {
  const itemName = formatCategoryName(item.name);
  const pathLabel = isMobile && item._depth > 0 && item._pathNames?.length
    ? item._pathNames.map(formatCategoryName).join(" / ")
    : "";

  return (
    <CommandItem
      value={itemName}
      keywords={[
        item.codigo,
        getCategoryCodeSegment(item),
        ...(item._pathNames || []),
        itemName,
      ].filter(Boolean)}
      className={cn(
        "min-w-0 gap-2",
        "min-h-12 px-3 py-2.5 text-base sm:min-h-0 sm:px-2 sm:py-1.5 sm:text-sm"
      )}
      style={{ paddingLeft: `${12 + (item._depth || 0) * 18}px` }}
      onSelect={onSelect}
    >
      <div
        className={cn("shrink-0 rounded-full", item._depth > 0 ? "h-2 w-2 opacity-70" : "h-3 w-3")}
        style={{ backgroundColor: item.color || stringToColor(itemName) }}
      />
      <span className="shrink-0 rounded border bg-muted px-1.5 py-0.5 font-mono text-[11px] font-semibold text-muted-foreground">
        {getCategoryCodeSegment(item)}
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn("block truncate", item._depth > 0 && "text-sm text-muted-foreground")}>
          {itemName}
        </span>
        {pathLabel && (
          <span className="block truncate text-xs text-muted-foreground/80">
            {pathLabel}
          </span>
        )}
      </span>
      <Check className={cn("h-4 w-4 shrink-0", selected ? "opacity-100" : "opacity-0")} />
      {onAddChild && (item._depth || 0) < CATEGORY_CODE_MAX_DEPTH - 1 && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="ml-1 h-8 w-8 shrink-0 text-muted-foreground hover:text-primary"
          aria-label={`Adicionar filho de ${itemName}`}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onAddChild(item);
          }}
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}
    </CommandItem>
  );
}

function AccountCommandItem({ item, selected, onSelect }) {
  return (
    <CommandItem
      value={item.name}
      className="min-h-12 min-w-0 gap-2 px-3 py-2.5 text-base sm:min-h-0 sm:px-2 sm:py-1.5 sm:text-sm"
      onSelect={onSelect}
    >
      <span className="min-w-0 flex-1 truncate">{item.name}</span>
      <Check className={cn("h-4 w-4 shrink-0", selected ? "opacity-100" : "opacity-0")} />
    </CommandItem>
  );
}

function GenericCommandItem({ item, selected, onSelect }) {
  return (
    <CommandItem
      value={item.name}
      className="min-h-12 min-w-0 gap-2 px-3 py-2.5 text-base sm:min-h-0 sm:px-2 sm:py-1.5 sm:text-sm"
      onSelect={onSelect}
    >
      <span className="min-w-0 flex-1 truncate">{item.name}</span>
      <Check className={cn("h-4 w-4 shrink-0", selected ? "opacity-100" : "opacity-0")} />
    </CommandItem>
  );
}

function SearchSelectCommand({
  label,
  value,
  displayValue,
  searchDisplayValue,
  renderedItems,
  isCategory,
  isAccount,
  isMobile,
  onCreate,
  onSelectItem,
  onAddChild,
  isSelected,
}) {
  const commandListClassName = isMobile
    ? "max-h-none overflow-visible scroll-py-1"
    : "max-h-[min(58dvh,460px)] touch-pan-y overscroll-contain scroll-py-1 overflow-y-auto";

  return (
    <CommandList
      className={commandListClassName}
      onWheel={(event) => event.stopPropagation()}
      onTouchMove={(event) => event.stopPropagation()}
      style={{
        WebkitOverflowScrolling: "touch",
        overscrollBehavior: "contain",
        touchAction: "pan-y",
      }}
    >
      <CommandEmpty>
        {value ? (
          <button
            type="button"
            className="flex min-h-12 w-full items-center gap-2 px-4 py-2 text-left text-base hover:bg-accent sm:min-h-0 sm:text-sm"
            onClick={onCreate}
          >
            <Plus className="h-4 w-4" />
            Criar "{searchDisplayValue || displayValue}"
          </button>
        ) : (
          `Digite para criar nova ${label.toLowerCase()}`
        )}
      </CommandEmpty>
      <CommandGroup>
        {renderedItems.map((item) => {
          const selected = isSelected(item);
          const selectItem = () => onSelectItem(item, selected);

          if (isCategory) {
            return (
              <CategoryCommandItem
                key={item.id || item.name}
                item={item}
                selected={selected}
                isMobile={isMobile}
                onSelect={selectItem}
                onAddChild={onAddChild}
              />
            );
          }

          if (isAccount) {
            return (
              <AccountCommandItem
                key={item.id || item.name}
                item={item}
                selected={selected}
                onSelect={selectItem}
              />
            );
          }

          return (
            <GenericCommandItem
              key={item.id || item.name}
              item={item}
              selected={selected}
              onSelect={selectItem}
            />
          );
        })}
      </CommandGroup>
    </CommandList>
  );
}

export function TransactionCombobox({ 
  label, 
  value, 
  setValue, 
  selectedId = null,
  setSelectedId = null,
  items, 
  open, 
  setOpen, 
  placeholder,
  testId,
  showColor = false,
  onAddChild,
}) {
  const [searchValue, setSearchValue] = useState("");
  const isMobileCombobox = useIsMobileCombobox();
  const isCategory = showColor || label.toLocaleLowerCase("pt-BR").includes("categoria");
  const isAccount = label.toLocaleLowerCase("pt-BR").includes("conta");
  const displayValue = isCategory && value ? formatCategoryName(value) : value;
  const normalizeValue = (name) => {
    if (isCategory) return normalizeCategoryName(name);
    if (isAccount) return normalizeUppercase(name);
    return name;
  };
  const renderedItems = useMemo(() => {
    if (isCategory) {
      return flattenCategoryTree(items, { includeCollapsedChildren: true });
    }

    if (isAccount) {
      return [...items].sort((a, b) => (
        (a.name || "").localeCompare(b.name || "", "pt-BR")
      ));
    }

    return items;
  }, [isAccount, isCategory, items]);
  const searchDisplayValue = isCategory && searchValue ? formatCategoryName(searchValue) : searchValue;
  const clearSelectedId = () => {
    if (setSelectedId) setSelectedId(null);
  };
  const handleSearchChange = (nextValue) => {
    const normalizedValue = normalizeValue(nextValue);
    clearSelectedId();
    setSearchValue(normalizedValue);
    setValue(normalizedValue);
  };
  const handleCreate = () => {
    clearSelectedId();
    setOpen(false);
  };
  const isSelected = (item) => selectedId
    ? selectedId === item.id
    : normalizeValue(value) === normalizeValue(item.name);
  const handleSelectItem = (item, selected) => {
    if (selected) {
      setValue("");
      clearSelectedId();
    } else {
      setValue(normalizeValue(item.name));
      if (setSelectedId) setSelectedId(item.id || null);
    }
    setOpen(false);
  };
  const handleAddChild = onAddChild
    ? (item) => {
        setOpen(false);
        onAddChild(item);
      }
    : undefined;

  useEffect(() => {
    if (open) {
      setSearchValue("");
    }
  }, [open]);

  const input = (
    <CommandInput
      placeholder={`Buscar ou criar ${label.toLowerCase()}...`}
      value={searchDisplayValue}
      onValueChange={handleSearchChange}
      className="h-12 text-base sm:h-11 sm:text-sm"
      autoComplete="off"
      autoFocus={isMobileCombobox}
    />
  );
  const commandList = (
    <SearchSelectCommand
      label={label}
      value={value}
      displayValue={displayValue}
      searchDisplayValue={searchDisplayValue}
      renderedItems={renderedItems}
      isCategory={isCategory}
      isAccount={isAccount}
      isMobile={isMobileCombobox}
      onCreate={handleCreate}
      onSelectItem={handleSelectItem}
      onAddChild={handleAddChild}
      isSelected={isSelected}
    />
  );
  const triggerButton = (
    <SearchSelectTrigger
      open={open}
      value={value}
      displayValue={displayValue}
      placeholder={placeholder}
      testId={testId}
      isMobile={isMobileCombobox}
      onMobileOpen={() => setOpen(true)}
    />
  );
  const desktopCommand = (
    <Command className="max-h-[min(70dvh,560px)]" filter={commandFilter}>
      <CommandInput
        placeholder={`Buscar ou criar ${label.toLowerCase()}...`}
        value={searchDisplayValue}
        onValueChange={handleSearchChange}
        className="h-12 text-base sm:h-11 sm:text-sm"
        autoComplete="off"
      />
      {commandList}
    </Command>
  );

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {isMobileCombobox ? (
        <>
          {triggerButton}
          <MobileCommandSheet
            open={open}
            onOpenChange={setOpen}
            title={label}
            input={input}
            commandProps={{ filter: commandFilter }}
          >
            {commandList}
          </MobileCommandSheet>
        </>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            {triggerButton}
          </PopoverTrigger>
          <PopoverContent
            className="z-[80] w-[--radix-popover-trigger-width] max-w-[calc(100vw-3rem)] max-h-[min(70dvh,560px)] overflow-hidden p-0"
            align="start"
          >
            {desktopCommand}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
